/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.1.3): toast.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import $ from 'jquery';
import EventHandler from '../../bootstrap/dom/event-handler.js';

import { cssAnimation } from '../../util/animate-css.js';
import MpBaseComponent from './base-component.js';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'toast';
const DATA_KEY = 'bs.toast';
const EVENT_KEY = `.${DATA_KEY}`;
const ESCAPE_KEY = 'Escape';

export const EVENT_MOUSEOVER = `mouseover${EVENT_KEY}`;
export const EVENT_MOUSEOUT = `mouseout${EVENT_KEY}`;
export const EVENT_FOCUSIN = `focusin${EVENT_KEY}`;
export const EVENT_FOCUSOUT = `focusout${EVENT_KEY}`;
export const EVENT_HIDE = `hide${EVENT_KEY}`;
export const EVENT_HIDDEN = `hidden${EVENT_KEY}`;
export const EVENT_SHOW = `show${EVENT_KEY}`;
export const EVENT_SHOWN = `shown${EVENT_KEY}`;
export const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY}`;

const ANIMATION_IN = 'fadeInRight';
const ANIMATION_OUT = 'fadeOutUp';
const ANIMATION_SPEED = 'faster';
const CLASS_NAME_SHOW = 'show';
// const CLASS_NAME_SHOWING = 'showing';

const COMPONENT_CONTAINER_DATA_ATTR = 'data-mp-toast-container';
const COMPONENT_CONTAINER_SELECTOR = `div[${COMPONENT_CONTAINER_DATA_ATTR}]`;

/**
 * @typedef {{
 * type?: 'notification'|'debug';
 * format?: 'primary'|'secondary'|'success'|'info'|'notice'|'warning'|'error';
 * icon?: boolean|string;
 * title?: string;
 * content?: string|Node|JQuery;
 * contentSelector?: string;
 * containerParent?: string|HTMLElement|JQuery;
 * positionX?: 'left'|'center'|'right';
 * positionY?: 'top'|'center'|'bottom';
 * fullWidth?: boolean;
 * keyboard?: boolean;
 * focus?: boolean;
 * dismissible?: boolean;
 * dismissActions?: MpDataAction[];
 * disposeOnHide?: boolean;
 * allowMinimize?: boolean;
 * clearPrev?: boolean;
 * clearPrevErrors?: boolean;
 * preventDuplicates?: boolean;
 * animated?: boolean;
 * autoAnimation?: boolean;
 * autohide?: boolean;
 * autohideError?: boolean;
 * delay?: number;
 * bodyClass?: string;
 * }} MpToastOptions
 */

/**
 * @type {MpToastOptions}
 */
export const Default = {
	containerParent: 'body',
	type: 'notification',
	format: 'info',
	icon: true,
	positionX: 'right',
	positionY: 'top',
	fullWidth: false,
	keyboard: true, // TODO: To be implemented
	focus: true,
	dismissible: true,
	disposeOnHide: true,
	allowMinimize: false,
	animated: true,
	autoAnimation: true,
	autohide: true,
	delay: 5000,
};

export const DefaultType = {
	containerParent: '(string|element)',
	type: 'string',
	format: 'string',
	icon: '(string|boolean)',
	positionX: 'string',
	positionY: 'string',
	keyboard: 'boolean',
	focus: 'boolean',
	// closable: 'boolean', // deprecated
	dismissible: 'boolean',
	disposeOnHide: 'boolean',
	allowMinimize: 'boolean',
	animated: 'boolean',
	autoAnimation: 'boolean',
	autohide: 'boolean',
	delay: 'number',
	fullWidth: 'boolean',
	bodyClass: 'string',
};

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

/**
 * Toast component
 * @class
 */
export default class MpToast extends MpBaseComponent {

	// ==============
	// Props
	// ==============

	// * =====
	// * NOTE: Don't put any props here because this is an extended class and the super constructor will be called before anything else and it will break everything
	// * =====

	// =======================================================================================

	// ==============
	// Static Getters
	// ==============

	static get NAME() {
		return NAME;
	}

	static get DefaultType() {
		return DefaultType;
	}

	static get Default() {
		return Default;
	}

	// ==============
	// Static Methods
	// ==============

	/**
	 * @param {MpToastOptions} options
	 * @inheritdoc
	 */
	static buildTemplate(options) {

		const varClose = mpro.langvar('button_close');

		let {
			type,
			format,
			title,
			icon,
			content,
			contentSelector,
			dismissible = true,
			allowMinimize = false,
			fullWidth,
			bodyClass,
		} = options;

		format = format === 'notice' ? 'info' : format; // 'notice' is used only on the backend, use 'info' instead
		icon = icon === true ? format : icon;

		const {
			toast_class,
			toast_format_class,
			wrapper_no_title_class,
			wrapper_title_class,
			title_class,
			body_class,
			icon_container_class,
		} = mpro.config.ui.toast;

		const toastAttr = [`class="${toast_class} ${toast_format_class.replaceAll('%format%', format)}${fullWidth ? ' toast-full-width' : ''}" role="alert" aria-live="assertive" aria-atomic="true" data-toast-type="${type}"`];
		const closeAttr = [`type="button" class="btn-close" data-bs-dismiss="toast" aria-label="${varClose}"`];
		const defaultBodyClass = [body_class];

		let iconHtml = '';
		let minimizeHtml = '';

		if (icon) {
			iconHtml = `<div class="${icon_container_class}">${mpro.icon(icon, { format })}</div>`;
		}

		// Unclosable instance
		if (dismissible === false) {
			// Hide the close button
			closeAttr.push('style="display: none;"');
		}

		// Display the minimize button (only if we can minimize the content)
		if ((allowMinimize === true) && title) {
			minimizeHtml = `
				<button type="button" class="btn-minimize" aria-label="${mpro.langvar('button_minimize', {}, false) || 'Minimize'}" data-button-minimize></button>
				<button type="button" class="btn-maximize" aria-label="${mpro.langvar('button_maximize', {}, false) || 'Maximize'}" data-button-maximize style="display: none;"></button>
			`;
		}

		// Add extra body class
		if (bodyClass) {
			defaultBodyClass.push(bodyClass);
		}

		// Load content from dom partial
		if (!content && contentSelector) {
			content = $(contentSelector).clone();
		}

		let nodeContent;
		let htmlContent;

		if (content instanceof Node || content instanceof jQuery) {
			nodeContent = content;
			content = '';
		}

		// Render toast with a title header
		// eslint-disable-next-line unicorn/prefer-ternary
		if (title) {
			htmlContent = `<div ${toastAttr.join(' ')}>
				<div class="${wrapper_title_class}">
					${iconHtml}
					<div class="${title_class}">${title}</div>
					${minimizeHtml}
					<button ${closeAttr.join(' ')}></button>
				</div>
				<div class="${defaultBodyClass.filter(Boolean).join(' ')}" data-mp-overlay-body>${content}</div>
			</div>`;
		}
		// Render toast without title, with enlarged body
		else {
			htmlContent = `<div ${toastAttr.join(' ')}>
				<div class="${wrapper_no_title_class}">
					${iconHtml}
					<div class="${defaultBodyClass.filter(Boolean).join(' ')}" data-mp-overlay-body>${content}</div>
					${minimizeHtml}
					<button ${closeAttr.join(' ')}></button>
				</div>
			</div>`;
		}

		const $template = $(htmlContent);

		// Add the node content
		if (nodeContent) {
			// @ts-ignore
			$template.find('div[data-mp-overlay-body]').append(nodeContent);
		}

		return $template;
	}

	/**
	 * @param {MpToastOptions} options
	 */
	static show(options) {
		const instance = new this(options);
		instance.show();
		return instance;
	}

	// ==============
	// Public Methods
	// ==============

	/**
	 * @inheritdoc
	 *
	 * @param {string|HTMLElement|MpToastOptions} elementOrConfig
	 * @param {MpToastOptions=} config
	 */
	init(elementOrConfig, config) {

		/** @type {MpToastOptions} */
		this._config = undefined;
		/** @type {number} */
		this._timeout = null;
		/** @type {boolean} */
		this._hasMouseInteraction = false;
		/** @type {boolean} */
		this._hasKeyboardInteraction = false;

		/**
		 * Escape key handler
		 *
		 * ? This needs to be initialized here because it's used in the constructor
		 * ? Defining it as a class property will cause it to be undefined when the constructor is called
		 * ? `_setListeners` uses this handler and is called in the constructor
		 *
		 * @param {JQuery.TriggeredEvent} event
		 */
		this._dismissHandler = (event) => {
			if (this._config.keyboard && event.key === ESCAPE_KEY) {
				this.hide();
			}
		};

		this._mouseOverInteractionHandler = /** @param {MouseEvent} event */ event => this._onInteraction(event, true);
		this._mouseOutInteractionHandler = /** @param {MouseEvent} event */ event => this._onInteraction(event, false);
		this._focusInInteractionHandler = /** @param {FocusEvent} event */ event => this._onInteraction(event, true);
		this._focusOutInteractionHandler = /** @param {FocusEvent} event */ event => this._onInteraction(event, false);

		// Init base
		super.init(elementOrConfig, config);
		// Add all listeners to the element
		this._setListeners();
	}

	/**
	 * @param {MpToastOptions} config
	 */
	_getConfig(config) {

		// Normalize config
		config = super._getConfig(config);

		// Unclosable instance
		if (config.dismissible === false) {
			// No Escape key
			config.keyboard = false;
		}

		return config;
	}

	/**
	 * Show the element w/o animation
	 */
	show() {

		// TODO: decide if we should use reInit for this method only
		if (this._isDestroyed) {
			// @ts-ignore
			// console.warn('[%s] component instance is destroyed: %o', this.constructor.NAME, this);
			// return this;
			this.reInit();
		}

		const element = this._element;
		const showEvent = EventHandler.trigger(element, EVENT_SHOW);

		if (showEvent.defaultPrevented) {
			return this;
		}

		// End current transition
		this._endAnimation();

		// Remove timers
		this._clearTimeout();

		// Make sure the element is added (and the container is also added)
		this._attachElement();

		// Start animation if enabled
		this._startAnimation(this._getAnimationIn()).done((_) => {
			// element.classList.remove(CLASS_NAME_SHOWING);
			EventHandler.trigger(element, EVENT_SHOWN);
			this._isShown = true;

			this._maybeScheduleHide();
		});

		element.classList.add(CLASS_NAME_SHOW);
		// element.classList.add(CLASS_NAME_SHOWING);

		return this;
	}

	/**
	 * Hide the element w/o animation
	 *
	 * @param {boolean} forceNoTransition
	 */
	hide(forceNoTransition = false) {

		if (this._isDestroyed) {
			// @ts-ignore
			console.warn('[%s] component instance is destroyed: %o', this.constructor.NAME, this);
			return this;
		}

		const element = this._element;

		if (!element.classList.contains(CLASS_NAME_SHOW)) {
			return this;
		}

		const hideEvent = EventHandler.trigger(element, EVENT_HIDE);
		if (hideEvent.defaultPrevented) {
			return this;
		}

		this._isShown = false;

		// End current transition
		this._endAnimation();

		// Remove timers
		this._clearTimeout();

		// The end animation callback might destroy the instance
		if (this._isDestroyed) {
			return this;
		}

		// Execute the dismiss action
		if (this._config.dismissActions) {
			this._handleDismissActions(this._config.dismissActions);
		}

		// Start animation if enabled
		this._startAnimation(this._getAnimationOut(), forceNoTransition).done((_) => {
			// element.classList.remove(...[CLASS_NAME_SHOWING, CLASS_NAME_SHOW]);
			element.classList.remove(CLASS_NAME_SHOW);
			EventHandler.trigger(element, EVENT_HIDDEN);

			this._maybeDetachElement();

			// Dispose on hide
			if (!this._isDestroyed && this._config.disposeOnHide) {
				this.dispose();
			}
		});

		return this;
	}

	/**
	 * Close & dispose the instance
	 *
	 * @param {boolean} forceNoTransition
	 */
	close(forceNoTransition = true) {
		this.hide(forceNoTransition).getAnim().done(_ => this.dispose());
		return this;
	}

	dispose() {
		// Make sure there are no animations running
		this._endAnimation();
		// Remove timers
		this._clearTimeout();

		// Already disposed
		// ! Note: This MUST be called after endAnim() method because if will also call dispose() and it might skip the _isDestroyed check
		if (this._isDestroyed) {
			return;
		}

		// Cleanup listeners
		this._unsetListeners();

		const element = this._element;

		if (element.classList.contains(CLASS_NAME_SHOW)) {
			element.classList.remove(CLASS_NAME_SHOW);
		}

		this._maybeDetachElement();

		super.dispose();
	}

	// ==========================
	// Private/Protected Methods
	// ==========================

	_attachElement() {

		const {
			container_class,
			container_position_class,
		} = mpro.config.ui.toast;

		const { positionX, positionY, containerParent } = this._config;

		const defaultContainerClass = [
			container_class,
			container_position_class.replaceAll('%axis%', 'x').replaceAll('%position%', positionX),
			container_position_class.replaceAll('%axis%', 'y').replaceAll('%position%', positionY),
		];

		/** @type {JQuery} */ // @ts-ignore
		const $containerParent = $(containerParent);
		let $container = $(COMPONENT_CONTAINER_SELECTOR, $containerParent).filter('.' + defaultContainerClass.join('.'));

		// Add new container
		if ($container.length === 0) {
			$container = $(`<div class="${defaultContainerClass.join(' ')}" ${COMPONENT_CONTAINER_DATA_ATTR}></div>`);
			$container.appendTo($containerParent);
		}

		// Add the element
		$container.append(this._element);

		return $container;
	}

	_maybeDetachElement() {

		// Component is destroyed
		if (this._isDestroyed) {
			return;
		}

		if (!this._element || !this._element.parentNode) {
			return;
		}

		const $element = $(this._element);
		const $container = $element.parent();

		// Remove the whole container
		if ($container.children().length <= 1) {
			$container.remove();
		}
		// Remove just the element
		else {
			$element.remove();
		}
	}

	_maybeScheduleHide() {

		// Component is destroyed
		if (this._isDestroyed) {
			return;
		}

		if (!this._config.autohide) {
			return;
		}

		if (this._hasMouseInteraction || this._hasKeyboardInteraction) {
			return;
		}

		// Clear the timeout first
		this._clearTimeout();
		this._timeout = window.setTimeout(() => {
			this._timeout = null;
			this.hide();
		}, this._config.delay);
	}

	_clearTimeout() {
		if (this._timeout) {
			window.clearTimeout(this._timeout);
			this._timeout = null;
		}
	}

	/**
	 * @param {MouseEvent | FocusEvent} event
	 * @param {*} isInteracting
	 */
	_onInteraction(event, isInteracting) {
		switch (event.type) {
			case 'mouseover':
			case 'mouseout': {
				this._hasMouseInteraction = isInteracting;
				break;
			}
			case 'focusin':
			case 'focusout': {
				this._hasKeyboardInteraction = isInteracting;
				break;
			}
			default: {
				break;
			}
		}

		if (isInteracting) {
			this._clearTimeout();
			return;
		}

		/** @type {HTMLElement} */ // @ts-ignore
		const nextElement = event.relatedTarget;
		if (this._element === nextElement || this._element.contains(nextElement)) {
			return;
		}

		this._maybeScheduleHide();
	}

	_setListeners() {
		EventHandler.on(this._element, EVENT_MOUSEOVER, this._mouseOverInteractionHandler);
		EventHandler.on(this._element, EVENT_MOUSEOUT, this._mouseOutInteractionHandler);
		EventHandler.on(this._element, EVENT_FOCUSIN, this._focusInInteractionHandler);
		EventHandler.on(this._element, EVENT_FOCUSOUT, this._focusOutInteractionHandler);

		const $element = $(this._element);
		const $btnMinimize = $element.find('[data-button-minimize]');
		const $btnMaximize = $element.find('[data-button-maximize]');
		const $toastBody = $element.find('[data-mp-overlay-body]');

		$btnMinimize.on('click', (e) => {
			$btnMinimize.hide();
			$btnMaximize.show();
			$toastBody.slideUp('fast');
		});

		$btnMaximize.on('click', (e) => {
			$btnMinimize.show();
			$btnMaximize.hide();
			$toastBody.slideDown('fast');
		});

		// TODO: implement focus on container + a special debug container, separate from the toast container
		// TODO: attach the focus event on the element instead of the container
		$(document.body).on(EVENT_KEYDOWN_DISMISS, this._dismissHandler);
	}

	_unsetListeners() {
		EventHandler.off(this._element, EVENT_MOUSEOVER, this._mouseOverInteractionHandler);
		EventHandler.off(this._element, EVENT_MOUSEOUT, this._mouseOutInteractionHandler);
		EventHandler.off(this._element, EVENT_FOCUSIN, this._focusInInteractionHandler);
		EventHandler.off(this._element, EVENT_FOCUSOUT, this._focusOutInteractionHandler);
		$(document.body).off(EVENT_KEYDOWN_DISMISS, this._dismissHandler);
	}

	_getAnimationIn() {

		const {
			autoAnimation,
			positionX,
			positionY,
		} = this._config;

		if (!autoAnimation) {
			return ANIMATION_IN;
		}

		// From left
		if (positionX === 'left') {
			return 'fadeInLeft';
		}

		// From center
		if (positionX === 'center') {
			if (['top', 'center'].includes(positionY)) {
				return 'fadeInDown';
			}
			return 'fadeInUp';
		}

		// From right
		return 'fadeInRight';
	}

	_getAnimationOut() {

		const {
			autoAnimation,
			positionX,
			positionY,
		} = this._config;

		if (!autoAnimation) {
			return ANIMATION_OUT;
		}

		// From left
		if (positionX === 'left') {
			return 'fadeOutLeft';
		}

		// From center
		if (positionX === 'center') {
			if (['top', 'center'].includes(positionY)) {
				return 'fadeOutUp';
			}
			return 'fadeOutDown';
		}

		// From right
		return 'fadeOutRight';
	}

	/**
	 * @param {string} animClass
	 * @param {boolean?} forceNoTransition
	 */
	_startAnimation(animClass, forceNoTransition = false) {
		this._ac = cssAnimation(this._element, animClass, ANIMATION_SPEED, '', this._config.animated && (forceNoTransition !== true)).always((_) => {
			this._ac = null;
		});
		return this._ac;
	}

	_endAnimation() {
		if (this._ac) {
			this._ac.resolve(this._element);
		}

		return this;
	}
}

// Add init function that is executed only on first dom-ready (it should contain only document/body delegate handlers)
mpro.addInitDelegate(() => {
	// Delegate handler to enable closing instances
	MpToast.enableDismissTrigger();
});

// @ts-ignore
// window.MpToast = MpToast;